import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import './EventGalleryDisplay.css';
import Footer from '../components/Footer';
const EventGalleryDisplay = () => {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (slug) {
      fetchGallery();
    }
  }, [slug]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/event-gallery/${slug}`);
      setGallery(response.data);
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevImage = () => {
    setSelectedImageIndex(prevIndex =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNextImage = () => {
    setSelectedImageIndex(prevIndex =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowLeft') goToPrevImage();
      if (e.key === 'ArrowRight') goToNextImage();
      if (e.key === 'Escape') closeLightbox();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, images.length]);

  if (loading) {
    return <div className="loading">Loading gallery...</div>;
  }

  if (!gallery) {
    return (
      <div className="gallery-error">
        <h2>Gallery not found</h2>
        <p>This gallery does not exist or has been deleted.</p>
      </div>
    );
  }

  return (
    <>
    <div className="gallery-display">
      <div className="gallery-container">
        <a href="/" className="back-button">← Back to Home</a>

        <div className="gallery-header">
          <h1>{gallery.event_name}</h1>
          {gallery.event_date && (
            <p className="gallery-date">
              {new Date(gallery.event_date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
          {gallery.event_description && (
            <p className="gallery-description">{gallery.event_description}</p>
          )}
        </div>

        {images.length === 0 ? (
          <div className="no-images">
            <p>No images available for this gallery yet.</p>
          </div>
        ) : (
          <>
            <div className="gallery-grid">
              {images.map((image, idx) => (
                <div
                  key={idx}
                  className="gallery-item"
                  onClick={() => openLightbox(idx)}
                >
                  <img
                    src={image.image_path}
                    alt={`Gallery Image ${idx + 1}`}
                    loading="lazy"
                  />
                  <div className="gallery-item-overlay">
                    <span className="view-icon">🔍</span>
                  </div>
                </div>
              ))}
            </div>

            {selectedImageIndex !== null && (
              <div className="lightbox" onClick={closeLightbox}>
                <span
                  className="lightbox-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeLightbox();
                  }}
                >
                  ×
                </span>

                <div
                  className="lightbox-content"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={images[selectedImageIndex].image_path}
                    alt={`Gallery Image ${selectedImageIndex + 1}`}
                  />
                </div>

                <span
                  className="lightbox-nav lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevImage();
                  }}
                >
                  &#10094;
                </span>

                <span
                  className="lightbox-nav lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNextImage();
                  }}
                >
                  &#10095;
                </span>

                <div className="lightbox-counter">
                  {selectedImageIndex + 1} / {images.length}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
      <Footer />
    </>
  );
};

export default EventGalleryDisplay;